import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaServer, FaNetworkWired, FaLock, FaHeadset, FaTools, FaProjectDiagram,
  FaArrowRight, FaHardHat, FaUserShield, FaShieldAlt, FaLayerGroup
} from 'react-icons/fa';

const Services = () => {
  const services = [
    {
      icon: <FaLayerGroup size={40} />,
      title: 'Data Center Construction & Commissioning',
      description: 'End-to-end construction and commissioning management — from L1–L5 commissioning through IST, RFS, and TCO support.',
      gradient: 'from-blue-700 to-cyan-500',
      link: '/services/data-center-commissioning'
    },
    {
      icon: <FaShieldAlt size={40} />,
      title: 'Data Center Security',
      description: 'End-to-end integrated security — CCTV, access control, biometrics, on-site guards, and 24/7 SOC monitoring for your facility.',
      gradient: 'from-slate-600 to-blue-700',
      link: '/services/data-center-security'
    },
    {
      icon: <FaUserShield size={40} />,
      title: 'Safety Professionals',
      description: 'Expert on-site safety professionals ensuring OSHA compliance, zero incidents, and full regulatory readiness for mission-critical environments.',
      gradient: 'from-amber-500 to-orange-600',
      link: '/services/safety-professionals'
    },
    {
      icon: <FaHardHat size={40} />,
      title: 'PPE Solutions',
      description: 'Complete Personal Protective Equipment solutions for data center safety and compliance.',
      gradient: 'from-yellow-600 to-amber-500',
      link: '/services/ppe-solutions'
    },
    /* {
      icon: <FaBroom size={40} />,
      title: 'Janitorial Staffing',
      description: 'Trained janitorial resources specialized in anti-static cleaning, raised floor maintenance, and contamination prevention for data centers.',
      gradient: 'from-teal-600 to-cyan-500',
      link: '/services/janitorial-staffing'
    }, */
    {
      icon: <FaServer size={40} />,
      title: 'Data Center',
      description: 'Enterprise-grade data center services with 24/7 monitoring and world-class infrastructure.',
      gradient: 'from-blue-600 to-cyan-500',
      link: '/data-center'
    },
    {
      icon: <FaNetworkWired size={40} />,
      title: 'Structured Cabling',
      description: 'Professional structured cabling systems designed for scalability and peak performance.',
      gradient: 'from-cyan-500 to-teal-500',
      link: '/services/network-cabling'
    },
    {
      icon: <FaLock size={40} />,
      title: 'Access Control',
      description: 'Secure access control systems for complete premises protection and safety.',
      gradient: 'from-indigo-600 to-blue-500',
      link: '/services/access-control'
    },
    {
      icon: <FaHeadset size={40} />,
      title: '24/7 Support',
      description: 'Round-the-clock technical support and maintenance services for your peace of mind.',
      gradient: 'from-purple-600 to-indigo-500',
      link: '/services/it-support'
    },
    {
      icon: <FaTools size={40} />,
      title: 'Break and Fix',
      description: 'Rapid response repair and maintenance for all telecommunications systems.',
      gradient: 'from-orange-500 to-red-500',
      link: '/services/break-fix'
    },
    {
      icon: <FaProjectDiagram size={40} />,
      title: 'Network Infrastructure',
      description: 'Advanced network infrastructure and cabling solutions for seamless connectivity.',
      gradient: 'from-teal-500 to-green-500',
      link: '/services/infrastructure'
    },
  ];

  return (
    <section id="services" className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-blue-100 rounded-full filter blur-3xl opacity-30 -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-100 rounded-full filter blur-3xl opacity-30 translate-x-1/2 translate-y-1/2"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-16 gap-6">
          <div>
            <div className="inline-block mb-4 px-4 py-2 bg-gradient-to-r from-blue-100 to-cyan-100 border border-blue-200 rounded-full">
              <span className="text-blue-600 text-sm font-bold tracking-wider">OUR SERVICES</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              Product and Services
            </h2>
          </div>
          <Link
            to="/contact"
            className="group px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-full font-semibold hover:shadow-lg hover:shadow-blue-500/30 transition-all duration-300 flex items-center gap-2"
          >
            Contact Us
            <FaArrowRight className="group-hover:translate-x-1 transition-transform" size={14} />
          </Link>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <div
              key={index}
              className="group relative bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-2xl hover:shadow-blue-500/10 transition-all duration-500 hover:-translate-y-2"
            >
              {/* Card Content */}
              <div className="p-8">
                {/* Icon */}
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  {service.icon}
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                  {service.title}
                </h3>

                {/* Description */}
                <p className="text-gray-600 leading-relaxed mb-6">
                  {service.description}
                </p>

                {/* Link */}
                <Link
                  to={service.link}
                  className="inline-flex items-center gap-2 text-blue-600 font-semibold group-hover:text-cyan-600 transition-colors"
                >
                  Learn More
                  <FaArrowRight className="group-hover:translate-x-2 transition-transform" size={12} />
                </Link>
              </div>

              {/* Bottom gradient line */}
              <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${service.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`}></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
